import { useEffect, useState } from 'react';
import Spinner from '../../components/UI/Spinner/Spinner';
import {
  DiagnosticResult,
  fetchDiagnosticResults,
  selectDiagnosticResults,
  selectDiagnosticResultsStatus,
} from '../../store/slices/diagnosticResults';
import {
  fetchSalesOrders,
  SalesOrderInfo,
  selectSalesOrders,
  selectSalesOrdersStatus,
} from '../../store/slices/salesOrders';
import { fetchServicesWithBiomarkerInfo } from '../../store/slices/services';
import { useAppDispatch, useAppSelector } from '../../store/store';
import searchIcon from '../../assets/icons/search.svg';
import slidersIcon from '../../assets/icons/sliders.svg';
import scanIcon from '../../assets/icons/scan-alt.svg';
import classes from './HomeTest.module.css';
import { ReportTable } from '../../components/UI/Layouts/ReportTable';
import EditSampleBarcodeModal from '../CheckIn/Modals/EditSampleBarcodeModal';
import ScanSampleBarcodeModal from '../CheckIn/Modals/ScanSampleBarcodeModal';
import Modal from '../../components/UI/Modals/Modal';
import PrimaryButtonV2 from '../../components/UI/Buttons/PrimaryButtonV2';
import { useTranslation } from '../../hooks/useTranslations';

const HomeTest: React.FC<any> = () => {
  const dispatch = useAppDispatch();
  const fetchSalesOrdersStatus = useAppSelector<string>(selectSalesOrdersStatus);
  const fetchDiagnosticResultsStatus = useAppSelector<string>(selectDiagnosticResultsStatus);
  const salesOrders: SalesOrderInfo[] = useAppSelector<SalesOrderInfo[]>(selectSalesOrders);
  const diagnosticResults: DiagnosticResult[] =
    useAppSelector<DiagnosticResult[]>(selectDiagnosticResults);
  const [isFetchingSalesOrders, setIsFetchingSalesOrders] = useState<boolean>(true);
  const [enhancedSalesOrders, setEnhancedSalesOrders] = useState<SalesOrderInfo[]>([]);
  const [displaySalesOrders, setDisplaySalesOrders] = useState<SalesOrderInfo[]>([]);
  const [isEditingCheckIn, setIsEditingCheckIn] = useState<boolean>(false);
  const [salesOrderEditing, setSalesOrderEditing] = useState<SalesOrderInfo>();
  const [isScanningSampleBarcode, setIsScanningSampleBarcode] = useState<boolean>(false);
  const [isSearchingManually, setIsSearchingManually] = useState<boolean>(false);
  const [searchBarcodeValue, setSearchBarcodeValue] = useState<string>('');
  const { translations } = useTranslation();

  useEffect(() => {
    dispatch(fetchSalesOrders());
    dispatch(fetchDiagnosticResults());
    dispatch(fetchServicesWithBiomarkerInfo());
  }, [dispatch]);

  useEffect(() => {
    if (fetchSalesOrdersStatus === 'pending') {
      setIsFetchingSalesOrders(true);
    } else if (fetchSalesOrdersStatus === 'fulfilled' || fetchSalesOrdersStatus === 'rejected') {
      setIsFetchingSalesOrders(false);
    }
  }, [fetchSalesOrdersStatus]);

  useEffect(() => {
    if (fetchDiagnosticResultsStatus === 'fulfilled' && fetchSalesOrdersStatus === 'fulfilled') {
      const tempoSalesOrders: SalesOrderInfo[] = [];
      for (const saleOrder of salesOrders.filter(
        (salesOrder) =>
          salesOrder?.work_order?.work_order_status_id === 1 &&
          salesOrder?.product_group?.product_group_id === 6 &&
          salesOrder.work_order
      )) {
        if (
          saleOrder.work_order_service_diagnostic &&
          diagnosticResults.filter(
            (d) => d.work_order_id == saleOrder.work_order_service_diagnostic?.work_order_id
          ).length > 0
        ) {
          tempoSalesOrders.push({
            ...saleOrder,
            diagnostic_results: saleOrder.diagnostic_results
              ? [
                  ...saleOrder.diagnostic_results,
                  ...diagnosticResults.filter(
                    (d) => d.work_order_id == saleOrder.work_order_service_diagnostic?.work_order_id
                  ),
                ]
              : [
                  ...diagnosticResults.filter(
                    (d) => d.work_order_id == saleOrder.work_order_service_diagnostic?.work_order_id
                  ),
                ],
          });
        } else {
          tempoSalesOrders.push({ ...saleOrder });
        }
      }
      setEnhancedSalesOrders(tempoSalesOrders);
    }
  }, [fetchDiagnosticResultsStatus, fetchSalesOrdersStatus, diagnosticResults, salesOrders]);

  useEffect(() => {
    let tempoOrders = [...enhancedSalesOrders];
    tempoOrders.sort(
      (a, b) =>
        Date.parse(
          b.work_order_service_diagnostic?.check_in_time?.toString() ?? '01 Jan 1970 00:00:00 GMT'
        ) -
        Date.parse(
          a.work_order_service_diagnostic?.check_in_time?.toString() ?? '01 Jan 1970 00:00:00 GMT'
        )
    );
    setDisplaySalesOrders([...tempoOrders]);
  }, [enhancedSalesOrders]);

  const onSearchHandler = (scannedBarcode?: string) => {
    const compareWithBarcode = scannedBarcode ?? searchBarcodeValue;
    const tempoSalesOrder = displaySalesOrders.find(
      (salesOrder) =>
        salesOrder?.diagnostic_results &&
        salesOrder?.diagnostic_results[0]?.sample_barcode &&
        salesOrder?.diagnostic_results[0]?.sample_barcode === compareWithBarcode
    );
    if (tempoSalesOrder) {
      setSalesOrderEditing(tempoSalesOrder);
      setIsEditingCheckIn(true);
      setIsSearchingManually(false);
      setIsScanningSampleBarcode(false);
      setSearchBarcodeValue('');
    }
  };

  return (
    <div className={classes.container}>
      <div className={classes.checkInsTableContainer}>
        <div className={classes.checkInsTitle}>
          <div className={classes.checkInsTitleText}>{translations('self.checked')}</div>
          <div className={classes.checkInsTableIconsContainer}>
            <img src={searchIcon} className={classes.checkInsTableIcons} alt="search icon" />
            <img
              src={scanIcon}
              className={classes.checkInsTableIcons}
              alt="search icon"
              onClick={() => {
                setIsScanningSampleBarcode(true);
              }}
            />
            <img src={slidersIcon} className={classes.checkInsTableIcons} alt="sliders icon" />
          </div>
        </div>
        <div className={classes.dividerHorizontal} />
        <div>
          {isFetchingSalesOrders ? (
            <Spinner />
          ) : (
            <ReportTable
              columns={[
                {
                  name: 'ID',
                  path: 'sales_order.sales_order_id',
                  type: 'ID',
                },
                {
                  name: translations('check-time'),
                  path: 'work_order_service_diagnostic.check_in_time',
                  type: 'DATETIME',
                },
                {
                  name: translations('last_name'),
                  path: 'customer_person.person_lastname',
                  type: 'TEXT',
                },
                {
                  name: translations('first_name'),
                  path: 'customer_person.person_firstname',
                  type: 'TEXT',
                },
                {
                  name: translations('date_of_birth'),
                  path: 'customer_person.person_birthdate',
                  type: 'DATE',
                },
                {
                  name: translations('test_service'),
                  path: 'product.product_name',
                  type: 'TEXT',
                },
                {
                  name: translations('sample.id'),
                  path: 'diagnostic_results.0.sample_barcode',
                  type: 'TEXT',
                },
              ]}
              rows={displaySalesOrders}
              actions={[
                {
                  displayName: translations('edit'),
                  onClick: (row) => {
                    if ((row as SalesOrderInfo).work_order_service_diagnostic) {
                      setSalesOrderEditing(row as SalesOrderInfo);
                      setIsEditingCheckIn(true);
                    } else {
                    }
                  },
                },
              ]}
            />
          )}
        </div>
      </div>
      {isEditingCheckIn && (
        <EditSampleBarcodeModal
          isOpen={isEditingCheckIn}
          onClose={() => setIsEditingCheckIn(false)}
          onScanClick={() => {}}
          scanValue={''}
          onChangeScanValue={() => {}}
          sampleTakenId={
            salesOrderEditing?.diagnostic_results
              ? salesOrderEditing?.diagnostic_results[0].sample_taken_id ?? 0
              : 0
          }
          selectedSalesOrder={salesOrderEditing}
        />
      )}
      {isScanningSampleBarcode && (
        <ScanSampleBarcodeModal
          isOpen={isScanningSampleBarcode}
          onClose={() => setIsScanningSampleBarcode(false)}
          onSuccessScan={(decodedText: any) => {
            if (decodedText) {
              onSearchHandler(decodedText);
            }
            setIsScanningSampleBarcode(false);
          }}
          openManual={() => {
            setIsSearchingManually(true);
          }}
        />
      )}
      {isSearchingManually && (
        <Modal
          isOpen={isSearchingManually}
          containerPadding={'80px 60px 40px 60px'}
          onClose={() => setIsSearchingManually(false)}
          header={translations('search.manually').toString()}
          divider
        >
          <div className={classes.modalContent}>
            <div className={classes.searchInputContainer}>
              <label className={classes.searchInputLabel}>{translations('search.sample')}</label>
              <input
                value={searchBarcodeValue}
                onChange={(event) => {
                  setSearchBarcodeValue(event?.target?.value);
                }}
                className={classes.searchInput}
              />
            </div>
            <PrimaryButtonV2
              text={translations('search')}
              containerStyle={{
                marginTop: 10,
              }}
              buttonStyle={{
                height: 40,
                borderRadius: 8,
                fontWeight: 600,
                fontSize: 14,
              }}
              disabled={searchBarcodeValue === ''}
              onClick={() => onSearchHandler()}
            />
            <div
              className={classes.modalContentSwitchButton}
              onClick={() => {
                setIsScanningSampleBarcode(true);
                setIsSearchingManually(false);
              }}
            >
              {translations('search.qr')}
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default HomeTest;
